import { HttpErrorResponse } from '@angular/common/http';
import { CartResponse } from '@core/dto/cart.dto';
import { PhErrorResponse } from '@core/dto/ph-error';
import { PurchaseLimitRule } from '@core/dto/purchase-limit-rule.model';
import { createAction, props } from '@ngrx/store';
import { ProductRecommendation } from '../product/product-state-models';
import {
  AddToCartConfirmModelData,
  MultiplePurchaseLimitRuleItems,
  QuantityLimitErrorData,
} from './cart-state-models';

export enum CartActionTypes {
  resetCartState = '[Cart] Reset Cart State',

  fetchCart = '[Cart] Fetch Cart',
  fetchCartSuccess = '[Cart] Fetch Cart Success',
  fetchCartFailure = '[Cart] Fetch Cart Failure',

  addToCart = '[Cart] Add To Cart',
  addToCartSuccess = '[Cart] Add To Cart Success',
  addToCartFailure = '[Cart] Add To Cart Failure',

  updateOrderLine = '[Cart] Update Order Line',
  updateOrderLineSuccess = '[Cart] Update Order Line Success',
  updateOrderLineFailure = '[Cart] Update Order Line Failure',

  mergeCarts = '[Cart] Merge Carts',
  mergeCartsSuccess = '[Cart] Merge Carts Success',
  mergeCartsFailure = '[Cart] Merge Carts Failure',

  createCart = '[Cart] Create Cart',
  createCartSuccess = '[Cart] Create Cart Success',
  createCartFailure = '[Cart] Create Cart Failure',

  deleteFromCart = '[Cart] Delete from Cart',
  deleteFromCartSuccess = '[Cart] Delete from Cart Success',
  deleteFromCartFailure = '[Cart] Delete from Cart Failure',

  fetchProductsRecommendations = '[Cart] Fetch Products Recommendations',
  fetchProductsRecommendationsSuccess = '[Cart] Fetch Products Recommendations Success',
  fetchProductsRecommendationsFailure = '[Cart] Fetch Products Recommendations Failure',

  addSurpriseBuy = '[Cart] Add Surprise Buy',
  addSurpriseBuySuccess = '[Cart] Add Surprise Buy Success',
  addSurpriseBuyFailure = '[Cart] Add Surprise Buy Failure',

  setAddToCartConfirmModal = '[Product] Set Add To Cart Confirmation Modal',

  revertOrderLineQty = '[Cart] Revert Order Line Qty',

  setQuantityLimitErrorModal = '[Cart] Set Quantity Limit Error Modal',
  resetQuantityLimitErrorModal = '[Cart] Reset Quantity Limit Error Modal',

  setOrderTotalLimitError = '[Cart] Set Order Total Limit Error',

  patchOrderLineLimits = '[Cart] Patch Order Line Limits',

  setPurchaseLimitRuleModal = '[Product] Set Purchase Limit Rule Modal',
  resetPurchaseLimitRuleModal = '[Product] Reset Purchase Limit Rule Modal',

  setMultiplePurchaseLimitRuleItems = '[Product] Set Multiple Purchase Limit Rule Modal',
  resetMultiplePurchaseLimitRuleModal = '[Product] Reset Multiple Purchase Limit Rule Modal',

  setMultiplePurchaseLimitModalOpen = '[Product] Set Multiple Purchase Limit Rule Modal Open',
}

export const resetCartState = createAction(CartActionTypes.resetCartState);
/**
 * Quantity limit error modal (update order line)
 */
export const setQuantityLimitErrorModal = createAction(
  CartActionTypes.setQuantityLimitErrorModal,
  props<{ payload: QuantityLimitErrorData }>(),
);
export const resetQuantityLimitErrorModal = createAction(
  CartActionTypes.resetQuantityLimitErrorModal,
);
export const updateOrderLineQtyFailure = createAction(
  CartActionTypes.updateOrderLineFailure,
  props<{ error: HttpErrorResponse }>(),
);

export const revertOrderLineQty = createAction(
  CartActionTypes.revertOrderLineQty,
  props<{ orderLineId: number; actualQty: number }>(),
);
/**
 * Fetch cart
 */
export const fetchCart = createAction(CartActionTypes.fetchCart);
export const fetchCartSuccess = createAction(
  CartActionTypes.fetchCartSuccess,
  props<{ cart: CartResponse }>(),
);
export const fetchCartFailure = createAction(
  CartActionTypes.fetchCartFailure,
  props<{ error: PhErrorResponse }>(),
);

/**
 * Add product to cart
 */
export const addToCart = createAction(
  CartActionTypes.addToCart,
  props<{ payload: AddToCartConfirmModelData }>(),
);
export const addToCartSuccess = createAction(
  CartActionTypes.addToCartSuccess,
  props<{ payload: AddToCartConfirmModelData }>(),
);
export const addToCartFailure = createAction(
  CartActionTypes.addToCartFailure,
  props<{ error: PhErrorResponse }>(),
);

/**
 * Update order line quantity
 */
export const updateOrderLineQty = createAction(
  CartActionTypes.updateOrderLine,
  props<{
    orderLineId: number;
    quantity: number;
    quantityLimit: number;
  }>(),
);
export const updateOrderLineQtySuccess = createAction(
  CartActionTypes.updateOrderLineSuccess,
  props<{ cart: CartResponse }>(),
);

/**
 * Merge guest and user carts
 */
export const mergeCarts = createAction(CartActionTypes.mergeCarts);
export const mergeCartsSuccess = createAction(CartActionTypes.mergeCartsSuccess);
export const mergeCartsFailure = createAction(
  CartActionTypes.mergeCartsFailure,
  props<{ error: HttpErrorResponse }>(),
);

/**
 * Create new, empty cart
 */
export const newCart = createAction(CartActionTypes.createCart);
export const newCartSuccess = createAction(CartActionTypes.createCartSuccess);
export const newCartFailure = createAction(
  CartActionTypes.createCartFailure,
  props<{ error: HttpErrorResponse }>(),
);

/**
 * Delete item from cart
 */
export const deleteFromCart = createAction(
  CartActionTypes.deleteFromCart,
  props<{ orderLineId: number }>(),
);
export const deleteFromCartSuccess = createAction(
  CartActionTypes.deleteFromCartSuccess,
  props<{ cart: CartResponse }>(),
);
export const deleteFromCartFailure = createAction(
  CartActionTypes.deleteFromCartFailure,
  props<{ error: PhErrorResponse }>(),
);

/**
 * Fetch products recommendations
 */
export const fetchProductsRecommendations = createAction(
  CartActionTypes.fetchProductsRecommendations,
  props<{ skus: string[] }>(),
);
export const fetchProductsRecommendationsSuccess = createAction(
  CartActionTypes.fetchProductsRecommendationsSuccess,
  props<{ recommendations: ProductRecommendation[] }>(),
);
export const fetchProductsRecommendationsFailure = createAction(
  CartActionTypes.fetchProductsRecommendationsFailure,
  props<{ error: PhErrorResponse }>(),
);

/**
 * AddToCart confirm modal
 */
export const setAddToCartConfirmModal = createAction(
  CartActionTypes.setAddToCartConfirmModal,
  props<{ payload: AddToCartConfirmModelData }>(),
);

export const addSurpriseBuy = createAction(CartActionTypes.addSurpriseBuy);
export const addSurpriseBuySuccess = createAction(
  CartActionTypes.addSurpriseBuySuccess,
  props<{ cart: CartResponse }>(),
);
export const addSurpriseBuyFailure = createAction(
  CartActionTypes.addSurpriseBuyFailure,
  props<{ error: HttpErrorResponse }>(),
);

export const setOrderTotalLimitError = createAction(
  CartActionTypes.setOrderTotalLimitError,
  props<{ payload: QuantityLimitErrorData }>(),
);

export const patchOrderLineLimits = createAction(
  CartActionTypes.patchOrderLineLimits,
  props<{ limits: { sku: string; quantityLimit: number }[] }>(),
);

/**
 * Purchase limit rule modals
 */
export const setPurchaseLimitRuleModal = createAction(
  CartActionTypes.setPurchaseLimitRuleModal,
  props<{ payload: PurchaseLimitRule; product: AddToCartConfirmModelData; isCartEmpty: boolean }>(),
);
export const resetPurchaseLimitRuleModal = createAction(
  CartActionTypes.resetPurchaseLimitRuleModal,
);
export const setMultiplePurchaseLimitRuleItems = createAction(
  CartActionTypes.setMultiplePurchaseLimitRuleItems,
  props<{ payload: { items: MultiplePurchaseLimitRuleItems; isCartEmpty: boolean } }>(),
);
export const resetMultiplePurchaseLimitRuleModal = createAction(
  CartActionTypes.resetMultiplePurchaseLimitRuleModal,
);
export const setMultiplePurchaseLimitModalOpen = createAction(
  CartActionTypes.setMultiplePurchaseLimitModalOpen,
  props<{ shouldOpenModal: boolean }>(),
);
